// JavaScript files are compiled and minified during the build process to the assets/built folder. See available scripts in the package.json file.

// Import CSS
import "../css/index.css";
import "../css/lds/mobile-website.css";
import "../css/lds/archive-cards.css";
import "../css/lds/resources.css";
import "../css/ghost/review-refinements.css";
import accessibleBrand from "./accessibleBrand";
import highlightContrast from "./highlightContrast";

// Import JS
import menuOpen from "./menuOpen";
import applyHomeEyebrows from "./homeEyebrows";
import homeDiscovery from "./homeDiscovery";
import categoryPhotoCredits from "./categoryPhotoCredits";
import groupFooterNavigation from "./footerNavigation";
import mobileFooter from "./mobileFooter";
import mobileHomepage from "./mobileHomepage";
import desktopBrowserTheme from "./desktopBrowserTheme";
import appearance from "./appearance";
import actionArrows from "./actionArrows";
import "../css/lds/appearance.css";
import formatMarkdownTextBlocks from "./formatMarkdownText";


// Call the theme behaviour functions
accessibleBrand();
highlightContrast();
appearance();
applyHomeEyebrows();
homeDiscovery();
categoryPhotoCredits();
groupFooterNavigation();
mobileFooter();
mobileHomepage();
desktopBrowserTheme();
menuOpen();
formatMarkdownTextBlocks();
actionArrows();

const setTopbarHeight = () => {
    const topbar = document.querySelector(".gh-head");

    if (topbar && !document.body.classList.contains("gh-head-open")) {
        document.documentElement.style.setProperty("--lds-ghost-topbar-height", `${topbar.offsetHeight}px`);
    }
};

setTopbarHeight();
window.addEventListener("resize", setTopbarHeight);

if ("ResizeObserver" in window) {
    const topbar = document.querySelector(".gh-head");

    if (topbar) {
        new ResizeObserver(setTopbarHeight).observe(topbar);
    }
}

document.querySelectorAll(".lds-ghost-phone-link").forEach((link) => {
    const phone = link.textContent.replace(/[^\d+]/g, "");

    if (phone) {
        link.setAttribute("href", `tel:${phone}`);
    }
});

document.querySelectorAll(".lds-ghost-abn-link").forEach((link) => {
    const abn = link.textContent.replace(/\D/g, "");

    if (abn) {
        link.setAttribute("href", `https://abr.business.gov.au/ABN/View?abn=${abn}`);
    }
});

document.querySelectorAll(".lds-ghost-footer-connect").forEach((section) => {
    if (!section.querySelector(".lds-footer-social a")) {
        section.remove();
    }
});

const buildEntryToc = () => {
    const tocBlocks = Array.from(document.querySelectorAll("[data-lds-entry-toc]"));
    const content = document.querySelector(".gh-content");

    if (!tocBlocks.length || !content) {
        return;
    }

    const headings = Array.from(content.querySelectorAll("h2"))
        .filter((heading) => heading.textContent.trim().length > 0);

    if (headings.length < 2) {
        tocBlocks.forEach((toc) => toc.remove());
        return;
    }

    const slugCounts = new Map();
    const slugify = (value) => value
        .toLowerCase()
        .trim()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "section";

    const tocTargets = headings.map((heading) => {
        const cardTarget = heading.closest(".kg-header-card, .kg-signup-card");
        const target = cardTarget || heading;
        const label = heading.textContent.trim();

        if (target.id) {
            return {heading, target, label, isCardTarget: Boolean(cardTarget)};
        }

        const baseSlug = slugify(label);
        const count = slugCounts.get(baseSlug) || 0;
        slugCounts.set(baseSlug, count + 1);
        target.id = count ? `${baseSlug}-${count + 1}` : baseSlug;

        return {heading, target, label, isCardTarget: Boolean(cardTarget)};
    });

    tocBlocks.forEach((toc) => {
        const list = toc.querySelector(".lds-ghost-entry-toc-list");

        if (!list) {
            toc.remove();
            return;
        }

        list.innerHTML = `
            <li>
                <a href="#top" data-lds-entry-toc-top>Top</a>
            </li>
        ${tocTargets.map(({target, label}) => `
            <li>
                <a href="#${target.id}">${label}</a>
            </li>
        `).join("")}`;
        toc.hidden = false;
    });

    const tocLinks = Array.from(document.querySelectorAll(".lds-ghost-entry-toc-list a"));
    const setActiveTocLink = (id) => {
        tocLinks.forEach((link) => {
            const isActive = link.getAttribute("href") === `#${id}`;

            if (isActive) {
                link.setAttribute("aria-current", "true");
            } else {
                link.removeAttribute("aria-current");
            }
        });
    };

    const updateHeadingOffset = () => {
        const visibleTopToc = document.querySelector(".lds-ghost-entry-toc--top:not([hidden])");
        const tocHeight = visibleTopToc ? visibleTopToc.offsetHeight : 0;
        const scrollMargin = tocHeight;

        tocTargets.forEach(({target, isCardTarget}) => {
            target.style.scrollMarginTop = `${scrollMargin + (isCardTarget ? 0 : 16)}px`;
        });
    };

    updateHeadingOffset();
    window.addEventListener("resize", updateHeadingOffset);

    const updateActiveFromScroll = () => {
        const offset = (document.querySelector(".lds-ghost-entry-toc--top:not([hidden])")?.offsetHeight || 0) + 24;
        const currentTarget = tocTargets
            .slice()
            .reverse()
            .find(({target}) => target.getBoundingClientRect().top <= offset);

        if (currentTarget) {
            setActiveTocLink(currentTarget.target.id);
            return;
        }

        setActiveTocLink("top");
    };

    updateActiveFromScroll();
    window.addEventListener("scroll", updateActiveFromScroll, {passive: true});

    const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const scrollToY = (targetY) => {
        const startY = window.scrollY;
        const distance = targetY - startY;
        const duration = 1400;
        const startTime = performance.now();
        const easeInOutSine = (progress) => -(Math.cos(Math.PI * progress) - 1) / 2;

        const step = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            window.scrollTo(0, startY + distance * easeInOutSine(progress));

            if (progress < 1) {
                window.requestAnimationFrame(step);
            }
        };

        window.requestAnimationFrame(step);
    };
    const scrollToTarget = (target) => {
        const scrollMarginTop = Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
        const targetY = target.getBoundingClientRect().top + window.scrollY - scrollMarginTop;

        scrollToY(targetY);
    };

    tocLinks.forEach((link) => {
        link.addEventListener("click", (event) => {
            const hash = link.getAttribute("href");

            if (!hash || !hash.startsWith("#")) {
                return;
            }

            event.preventDefault();

            if (link.hasAttribute("data-lds-entry-toc-top")) {
                if (reducedMotionQuery.matches) {
                    window.scrollTo(0, 0);
                } else {
                    scrollToY(0);
                }
            } else {
                const target = document.getElementById(hash.slice(1));

                if (!target) {
                    return;
                }

                if (reducedMotionQuery.matches) {
                    target.scrollIntoView({behavior: "auto", block: "start"});
                } else {
                    scrollToTarget(target);
                }
            }

            if (hash === "#top") {
                window.history.pushState(null, "", window.location.pathname + window.location.search);
            } else {
                if (window.history && window.history.pushState) {
                    window.history.pushState(null, "", hash);
                } else {
                    window.location.hash = hash;
                }
            }

            setActiveTocLink(hash.slice(1));
        });
    });
};

buildEntryToc();

const enableSubtleParallax = () => {
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (motionQuery.matches) {
        return;
    }

    const parallaxSections = Array.from(document.querySelectorAll([
        ".lds-ghost-home-hero",
        ".lds-ghost-home-explore--cover",
        ".lds-ghost-home-story-pillar--cover",
        ".lds-ghost-home-involved--cover"
    ].join(",")));

    if (!parallaxSections.length) {
        return;
    }

    let ticking = false;

    const updateParallax = () => {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        parallaxSections.forEach((section) => {
            const rect = section.getBoundingClientRect();

            if (rect.bottom < 0 || rect.top > viewportHeight) {
                return;
            }

            const sectionMidpoint = rect.top + rect.height / 2;
            const viewportMidpoint = viewportHeight / 2;
            const progress = (sectionMidpoint - viewportMidpoint) / viewportHeight;
            const clampedProgress = Math.max(-1, Math.min(1, progress));
            const offset = clampedProgress * -24;

            section.style.setProperty("--lds-ghost-parallax-y", `${offset.toFixed(2)}px`);
        });

        ticking = false;
    };

    const requestUpdate = () => {
        if (!ticking) {
            window.requestAnimationFrame(updateParallax);
            ticking = true;
        }
    };

    updateParallax();
    window.addEventListener("scroll", requestUpdate, {passive: true});
    window.addEventListener("resize", requestUpdate);
};

enableSubtleParallax();
